import type { LevelDef } from '../level.ts';
import { prague } from './prague.ts';
import { kyoto } from './kyoto.ts';
import { lisbon } from './lisbon.ts';
import { barcelona } from './barcelona.ts';
import { sydney } from './sydney.ts';
import { istanbul } from './istanbul.ts';
import { sanfrancisco } from './sanfrancisco.ts';
import { amsterdam } from './amsterdam.ts';

/**
 * Campaign order. The lab and the app both read this list.
 *
 * Two things rise down this list, and one deliberately does not.
 *
 * *Length* rises: `baseline` is a score, but divided by what an arrival pays
 * on that level it comes to about ten arrivals at prague and twenty-five at
 * amsterdam. *Reward* rises with it: `scoreMul` runs 1 to 2.5, so the same jet
 * is worth 20 over prague and 50 over amsterdam, and a good run in a late city
 * beats a perfect one at home. `tests/levels.test.ts` guards both.
 *
 * *Pressure* — `capBase`/`capDiv`/`capMax` and `minSpawnGap` — is not a ladder
 * rung by rung, because how much traffic a city can carry depends on its
 * runways: prague's two crossing strips take arrivals 1.6 s apart happily,
 * while san francisco's two tight pairs will not. The lab is what says whether
 * a city is harder than the one before it. The one rule is that the busiest
 * skies belong to the second half: lisbon used to sit at eight airborne in
 * third place, level with amsterdam, and that alone made the third city play
 * as the hardest in the game.
 *
 * Pressure does rise *within* a city, though, and at the same rate everywhere:
 * `capDiv` is 4 on every level, so the sky gains a plane every four landings
 * and each city spends its last stretch at its own `capMax` — prague's last
 * arrival or two at five, kyoto's last few at six. That ramp is what "a bit
 * more challenging" turned out to mean in the lab: where a city's whole
 * length was spent one rung below its cap, the bots found it chill and so did
 * the hand. Two openings are fixed by a contract rather than by taste:
 * prague's and sydney's `capBase` and `spawnLead` are replayed byte for byte in
 * tests/contracts/render-calls.test.ts (the radar draws every announced gate
 * by its eta), so a faster opening on those two would have to go through the
 * architecture doc first.
 */
export const LEVELS: LevelDef[] = [prague, kyoto, lisbon, barcelona, sydney, istanbul, sanfrancisco, amsterdam];

export function levelById(id: string): LevelDef {
  const l = LEVELS.find(l => l.id === id);
  if (!l) throw new Error(`unknown level: ${id}`);
  return l;
}
